import { OrderForm } from "@/forms/order-form";
import { client } from "@/hono";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type OrderFormSchema, orderFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface AddOrderModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	defaultValues?: Partial<OrderFormSchema>;
	/**
	 * Which sites the project picker offers. The personal order list opens this modal with
	 * `ownProjects`, so the sites on offer are the ones its own rows stand on.
	 */
	projectScope?: "organization" | "ownProjects";
}

export function AddOrderModal({
	onClose,
	isOpen,
	onComplete,
	defaultValues,
	projectScope,
}: AddOrderModalProps) {
	const isProjectLocked = !!defaultValues?.projectId;

	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(orderFormSchema),
		defaultValues: {
			articles: [],
			loadCarriers: [],
			...defaultValues,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: OrderFormSchema) {
		const response = await client.stock.orders.$post({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.add_order")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="95vw"
			>
				<Modal.Body>
					<OrderForm
						isProjectLocked={isProjectLocked}
						projectScope={projectScope}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
